import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, TOKEN_KEY, formatApiErrorDetail, clearApiCache } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, object=auth
  const [settings, setSettings] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const check = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      loadSettings();
    } catch (e) {
      if (e.response) localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    }
  }, [loadSettings]);

  useEffect(() => {
    check();
  }, [check]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.twofa_required) {
        return { ok: false, twofa_required: true, pre_auth_token: data.pre_auth_token };
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      loadSettings();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const verify2fa = async (pre_auth_token, code) => {
    try {
      const { data } = await api.post("/auth/2fa/verify", { pre_auth_token, code });
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      loadSettings();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    localStorage.removeItem(TOKEN_KEY);
    await clearApiCache();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, settings, loadSettings, login, verify2fa, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
